import { ProfileComponentType } from "@authory-packages/core";
import { prepareURL } from "./prepareURL";

// Where a bare handle lives, for the platforms whose profile url is a simple prefix plus handle
const HANDLE_BASE_URL: Partial<Record<ProfileComponentType, string>> = {
    [ProfileComponentType.TwitterLink]: "https://x.com/",
    [ProfileComponentType.InstagramLink]: "https://www.instagram.com/",
    [ProfileComponentType.FacebookLink]: "https://www.facebook.com/",
    [ProfileComponentType.ThreadsLink]: "https://www.threads.net/@",
    [ProfileComponentType.TikTokLink]: "https://www.tiktok.com/@",
    [ProfileComponentType.PinterestLink]: "https://www.pinterest.com/",
    [ProfileComponentType.SoundcloudLink]: "https://soundcloud.com/",
    [ProfileComponentType.PatreonLink]: "https://www.patreon.com/",
    [ProfileComponentType.RedditLink]: "https://www.reddit.com/user/",
    [ProfileComponentType.TumblrLink]: "https://www.tumblr.com/",
    [ProfileComponentType.VimeoLink]: "https://vimeo.com/",
    [ProfileComponentType.YoutubeLink]: "https://www.youtube.com/@",
    [ProfileComponentType.BlueskyLink]: "https://bsky.app/profile/"
};

/**
 * Whether a stored value is already a usable absolute url.
 * @param value The raw profile component value
 * @returns True when the value carries a protocol
 */
function isAbsoluteUrl(value: string) {
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
}

/**
 * Builds the href for a profile social link. Values are user entered and are often a bare handle
 * rather than a url, which the browser would otherwise resolve against the current page.
 * @param type The profile component type, decides the platform base url
 * @param value The stored url or handle
 * @returns An absolute url, or an empty string when none can be derived
 */
export function getSocialProfileUrl(type: ProfileComponentType, value: string) {
    const trimmed = (value || "").trim();

    if (!trimmed) return "";
    if (type === ProfileComponentType.Phone) return `tel:${trimmed}`;
    if (type === ProfileComponentType.Mail) return `mailto:${trimmed}?subject=Hello`;
    if (isAbsoluteUrl(trimmed)) return trimmed;

    const prepared = prepareURL(trimmed);

    if (isAbsoluteUrl(prepared)) return prepared;

    // Not a url, so treat it as a handle on its own platform
    const base = HANDLE_BASE_URL[type];

    if (!base) return "";

    const handle = trimmed.replace(/^@+/, "");

    // A handle with whitespace or a path separator is not one, and guessing would send visitors nowhere
    if (/[\s/]/.test(handle)) return "";

    return `${base}${encodeURIComponent(handle)}`;
}
